import { Quote, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Card, SectionLabel } from "@/components/ui/Card";

interface Props {
  message: string;
  status?: "on_track" | "caution" | "rest";
  narrative?: string | null;
  fullReviewId?: number | null;
}

const STATUS_ACCENT: Record<string, string> = {
  on_track: "bg-amber",
  caution: "bg-amber-dark",
  rest: "bg-coral",
};

export function CoachSaysCard({
  message,
  status = "on_track",
  narrative,
  fullReviewId,
}: Props) {
  const { t } = useTranslation();
  const text = narrative && narrative.trim().length > 0 ? narrative : message;
  const isLink = !!fullReviewId;
  // Heuristic: if the text contains markdown markers, render it through
  // ReactMarkdown so **bold**, headings and bullet lists don't leak as raw
  // asterisks. Plain status messages render as a single styled paragraph.
  const hasMarkdown = /(\*\*|^#{1,3}\s|^[-*]\s|^\d+\.\s|`)/m.test(text);

  const inner = (
    <Card
      tone="navy"
      className={
        "relative overflow-hidden" +
        (isLink ? " transition-colors hover:bg-navy-deep group" : "")
      }
    >
      <div className="flex items-center gap-2">
        <span className={`h-2 w-2 rounded-full ${STATUS_ACCENT[status]}`} />
        <SectionLabel className="text-amber-light">{t("coach.says")}</SectionLabel>
      </div>
      <div className="mt-3 flex gap-3">
        <Quote className="mt-1 h-5 w-5 shrink-0 text-amber" strokeWidth={1.6} />
        {hasMarkdown ? (
          <div className="coach-markdown-content min-w-0 flex-1">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown>
          </div>
        ) : (
          <p className="coach-voice min-w-0 flex-1 whitespace-pre-wrap">{text}</p>
        )}
      </div>
      {isLink ? (
        <div className="mt-3 flex items-center justify-end gap-1 border-t border-cream-light/10 pt-2 text-[12px] text-amber-light group-hover:text-amber-light">
          {t("coach.read_full_review")}
          <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.8} />
        </div>
      ) : null}
    </Card>
  );

  return isLink ? (
    <Link to={`/reviews?open=${fullReviewId}`} className="block">
      {inner}
    </Link>
  ) : (
    inner
  );
}
